// ChatCardRouter (plan todo 23) — routes a parsed "card" chat item to its
// structured card. Malformed or missing tool results fall back to the generic
// EstimateCard so a bad payload never crashes the thread.

import { EstimateCard } from "../EstimateCard";
import type { LoggingFlowReviewItem } from "../../lib/logging-flow";
import {
  extractDataQuality,
  extractExpenditure,
  extractGoalTimeline,
  extractMealEstimate,
  extractMemoryResult,
  extractTodaySummary,
  extractWeeklyCheckin,
  extractWeightTrend,
  type ChatCardKind,
} from "./card-data";
import { MealEstimateCard } from "./MealEstimateCard";
import { TodaySummaryCard } from "./TodaySummaryCard";
import { WeightTrendCard } from "./WeightTrendCard";
import { ExpenditureInfoCard, GoalTimelineCard } from "./GoalTimelineCard";
import { CheckinCard } from "./CheckinCard";
import { MemoryResultCard } from "./MemoryResultCard";
import { ChatDataQualityCard } from "./ChatDataQualityCard";

export interface ChatCardHandlers {
  readonly onSwitchScreen?: (screen: string) => void;
  readonly onSendMessage?: (text: string) => void;
  readonly onStartReview?: (items: readonly LoggingFlowReviewItem[]) => void;
  readonly onRespondCheckin?: (checkinId: string, accept: boolean) => Promise<unknown>;
}

export interface ChatCardRouterProps extends ChatCardHandlers {
  readonly cardKind: ChatCardKind;
  readonly toolName: string;
  readonly args: unknown;
  readonly result: unknown;
}

export function ChatCardRouter({
  cardKind,
  toolName,
  args,
  result,
  onSwitchScreen,
  onSendMessage,
  onStartReview,
  onRespondCheckin,
}: ChatCardRouterProps) {
  switch (cardKind) {
    case "meal-estimate": {
      const data = extractMealEstimate(result);
      if (data === null) break;
      return (
        <MealEstimateCard
          data={data}
          {...(onStartReview !== undefined ? { onStartReview } : {})}
          {...(onSendMessage !== undefined ? { onSendMessage } : {})}
        />
      );
    }
    case "today-summary": {
      const data = extractTodaySummary(result);
      if (data === null) break;
      return <TodaySummaryCard data={data} {...(onSwitchScreen !== undefined ? { onSwitchScreen } : {})} />;
    }
    case "weight-trend": {
      const data = extractWeightTrend(result);
      if (data === null) break;
      return <WeightTrendCard data={data} {...(onSwitchScreen !== undefined ? { onSwitchScreen } : {})} />;
    }
    case "goal-timeline": {
      if (toolName === "getExpenditureInfo") {
        const data = extractExpenditure(result);
        if (data === null) break;
        return <ExpenditureInfoCard data={data} {...(onSwitchScreen !== undefined ? { onSwitchScreen } : {})} />;
      }
      const data = extractGoalTimeline(result);
      if (data === null) break;
      return <GoalTimelineCard data={data} {...(onSwitchScreen !== undefined ? { onSwitchScreen } : {})} />;
    }
    case "weekly-checkin": {
      const data = extractWeeklyCheckin(toolName, result);
      if (data === null) break;
      return (
        <CheckinCard
          data={data}
          {...(onRespondCheckin !== undefined ? { onRespondCheckin } : {})}
          {...(onSendMessage !== undefined ? { onSendMessage } : {})}
          {...(onSwitchScreen !== undefined ? { onSwitchScreen } : {})}
        />
      );
    }
    case "memory-result": {
      const data = extractMemoryResult(result);
      if (data === null) break;
      return <MemoryResultCard data={data} {...(onSendMessage !== undefined ? { onSendMessage } : {})} />;
    }
    case "data-quality": {
      const data = extractDataQuality(result);
      if (data === null) break;
      return <ChatDataQualityCard data={data} {...(onSwitchScreen !== undefined ? { onSwitchScreen } : {})} />;
    }
  }
  console.warn("[Frontend][ChatCard] Falling back to generic card", {
    cardKind,
    toolName,
  });
  return <EstimateCard toolName={toolName} args={args} />;
}
